import React from 'react';
import { Text, mergeClasses } from '@fluentui/react-components';
import { CheckmarkRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useStepIndicatorStyles } from './StepIndicator.styles';
import { iconSize } from '@/theme';
import type { FormStep } from '@store/models/booking';

export interface StepIndicatorProps {
  steps: FormStep[];
  currentStep: number;
  highestVisitedStep: number;
  onStepClick: (stepIndex: number) => void;
  disabled?: boolean;
  className?: string;
  /**
   * Opt-in to the Figma-aligned 2026 visual style (navy filled active step,
   * navy+check completed, neutral outlined future). Defaults to `false` so
   * existing call sites render the legacy look unchanged.
   */
  figmaVariant?: boolean;
  /**
   * Which steps the user is allowed to click on.
   * - `'visited-and-next'` (default): completed/visited steps plus the next step.
   * - `'visited-only'`: only completed/visited steps; the active step and any
   *   upcoming step are not clickable.
   */
  clickableMode?: 'visited-only' | 'visited-and-current' | 'visited-and-next';
  /**
   * When the viewport reaches this breakpoint, switch to compact mode: only
   * the active step's label is visually shown; the other labels remain in the
   * DOM (and announced by assistive technologies) but are visually hidden.
   * Defaults to `'never'` for backward compatibility. Use `'always'` to keep
   * compact mode active at every viewport size (e.g., when the stepper sits
   * in a constrained slot such as a hero banner's tip-card area).
   */
  compactBreakpoint?: 'tablet' | 'mobile' | 'never' | 'always';
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  highestVisitedStep,
  onStepClick,
  disabled = false,
  className,
  figmaVariant = false,
  clickableMode = 'visited-and-next',
  compactBreakpoint = 'never',
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useStepIndicatorStyles();

  const isClickable = (i: number) => {
    if (disabled) return false;
    if (clickableMode === 'visited-only') return i < currentStep && i <= highestVisitedStep;
    if (clickableMode === 'visited-and-current')
      return i === currentStep || (i < currentStep && i <= highestVisitedStep);
    return i <= highestVisitedStep + 1;
  };
  const isCompleted = (i: number) => i < currentStep && i <= highestVisitedStep;
  const isActive = (i: number) => i === currentStep;
  const compactLabelClass =
    compactBreakpoint === 'always'
      ? styles.stepLabelCompactAlways
      : compactBreakpoint === 'tablet'
        ? styles.stepLabelCompactTablet
        : compactBreakpoint === 'mobile'
          ? styles.stepLabelCompactMobile
          : undefined;
  const compactButtonClass =
    compactBreakpoint === 'always'
      ? styles.stepButtonCompactAlways
      : compactBreakpoint === 'tablet'
        ? styles.stepButtonCompactTablet
        : compactBreakpoint === 'mobile'
          ? styles.stepButtonCompactMobile
          : undefined;
  const compactSeparatorClass =
    compactBreakpoint === 'always'
      ? styles.separatorCompactAlways
      : compactBreakpoint === 'tablet'
        ? styles.separatorCompactTablet
        : compactBreakpoint === 'mobile'
          ? styles.separatorCompactMobile
          : undefined;

  return (
    <div className={mergeClasses(styles.root, figmaVariant && styles.rootFigma, className)}>
      <Text className={styles.stepText} aria-live="polite">
        {t('bookingForm.messages.stepIndicator', { current: currentStep + 1, total: steps.length })}
      </Text>
      <span className={mergeClasses(styles.separator, compactSeparatorClass)}>
        <ChevronRightRegular style={{ fontSize: iconSize(12) }} />
      </span>
      <div
        role="tablist"
        aria-label={t('bookingForm.messages.stepIndicator', {
          current: currentStep + 1,
          total: steps.length,
        })}
        className={styles.tablistInner}
      >
        {steps.map((step, i) => (
          <React.Fragment key={step.id}>
            <button
              type="button"
              role="tab"
              aria-selected={isActive(i)}
              disabled={!isClickable(i)}
              className={mergeClasses(
                styles.stepButton,
                compactButtonClass,
                !isClickable(i) && styles.stepButtonDisabled
              )}
              onClick={() => isClickable(i) && onStepClick(i)}
              tabIndex={isClickable(i) ? 0 : -1}
            >
              <span
                className={mergeClasses(
                  styles.stepCircle,
                  isActive(i) && styles.stepCircleActive,
                  isCompleted(i) && styles.stepCircleCompleted,
                  figmaVariant && styles.stepCircleFigma,
                  figmaVariant && isActive(i) && styles.stepCircleFigmaActive,
                  figmaVariant && isCompleted(i) && styles.stepCircleFigmaCompleted
                )}
              >
                {isCompleted(i) ? <CheckmarkRegular style={{ fontSize: iconSize(14) }} /> : i + 1}
              </span>
              <Text
                className={mergeClasses(
                  styles.stepLabel,
                  isActive(i) && styles.stepLabelActive,
                  compactLabelClass && !isActive(i) && compactLabelClass
                )}
              >
                {t(step.titleKey)}
              </Text>
            </button>
            {i < steps.length - 1 && (
              <span
                className={mergeClasses(styles.separator, compactSeparatorClass)}
                aria-hidden="true"
              >
                <ChevronRightRegular style={{ fontSize: iconSize(12) }} />
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

StepIndicator.displayName = 'StepIndicator';
